import { DOCUMENT, Inject, Injectable, Optional } from '@angular/core';
import {
  BootstrapperBase,
  TOKENS as COMMON_TOKENS,
  LogService,
} from '@trustedshops/tswp-core-common';
import { LIBRARY_NAME } from '../constants/library-name.constant';
import { DEFAULT_SCRIPT_LOAD_TIMEOUT_MS } from '../constants/script-load-timeout.constant';
import { InstanaLoggingBootstrapperConfig } from './instana-logging-bootstrapper.config';
import { InstanaScriptLoaderService } from '../services/instana-script-loader.service';
import { InstanaConfigService } from './instana-config.service';
import { InstanaTrackingService } from './instana-tracking.service';
import { setupErrorHandlersImmediately } from './instana-error-handlers';

// Re-export tokens and initializers for backward compatibility
export * from './instana-initialization.tokens';
export * from './instana-initializers';

/**
 * Bootstrapper for Instana EUM (End User Monitoring) initialization and configuration.
 *
 * This bootstrapper orchestrates Instana-specific initialization concerns:
 * - Script loading with timeout and error handling
 * - Console fallback when script fails to load
 * - Global error handlers setup (window.onerror, unhandledrejection)
 * - Instana EUM configuration (reporting URL, API key, allowed origins)
 * - User and route tracking setup
 *
 * The initialization is split into two phases:
 * - Early phase: Script loading and error handlers (no dependencies, runs first)
 * - Late phase: User/route tracking (requires IdentityService, RouteSourceService, PluginRouter)
 *
 * Architecture Note: Script loading is extracted to InstanaScriptLoaderService for better
 * separation of concerns and testability. The bootstrapper orchestrates initialization while
 * the service handles the low-level script loading details.
 */
@Injectable()
export class InstanaLoggingBootstrapper implements BootstrapperBase {
  public static readonly NAME: string = `${LIBRARY_NAME}:${InstanaLoggingBootstrapper.name}`;
  public static readonly SCRIPT_ID: string = 'instana-eum-script';

  /**
   * Readable version of the Instana EUM queue declaration snippet
   * (function(s,t,a,n){s[t]||(s[t]=a,n=s[a]=function(){n.q.push(arguments)},
   * n.q=[],n.v=2,n.l=1*new Date)})(window,"InstanaEumObject","ineum");
   * @param window
   */
  public static readonly declareInstanaEumQueue = (window: any): void => {
    // Use 'any' for window to safely access the properties we are creating.
    const w = window as any;

    const globalScope = w;
    const instanaObjectKey = 'InstanaEumObject';
    const queueFunctionName = 'ineum';

    // Check if the object already exists
    if (!globalScope[instanaObjectKey]) {
      // Assign 'ineum' to the check key
      globalScope[instanaObjectKey] = queueFunctionName;

      // Define the queue function
      const instanaQueueFunction: any = (globalScope[queueFunctionName] =
        function (...args: any[]) {
          // Queue command logic
          instanaQueueFunction?.q.push(args);
        });

      // Initialize properties
      instanaQueueFunction.q = []; // Command queue array
      instanaQueueFunction.v = 2; // Version marker
      instanaQueueFunction.l = Date.now(); // Load timestamp
    }
  };

  // Track if early initialization has been completed
  private _earlyInitialized = false;
  private _config: InstanaLoggingBootstrapperConfig | null = null;

  public constructor(
    @Inject(DOCUMENT) private readonly _document: Document,
    private readonly _scriptLoader: InstanaScriptLoaderService,
    private readonly _configService: InstanaConfigService,
    private readonly _trackingService: InstanaTrackingService,
    @Optional()
    @Inject(COMMON_TOKENS.LogService)
    private readonly _logService?: LogService
  ) {}

  /**
   * Early initialization phase: Loads Instana script and sets up global error handlers.
   * This phase has no dependencies and should run first.
   *
   * CRITICAL: Global error handlers are set up synchronously at the start to ensure
   * they're active before any other code runs, even if script loading is async.
   */
  public async initializeEarly(
    config?: InstanaLoggingBootstrapperConfig
  ): Promise<void> {
    if (!config) {
      this.logWarning('Config is not provided, cannot initialize Instana.');
      return;
    }

    if (this._earlyInitialized) {
      this.logInformation('Instana early initialization already completed.');
      return;
    }

    this._config = config;

    // CRITICAL: Set up global error handlers FIRST, synchronously
    // This ensures they're active before any other initializers can run
    // Even if script loading fails, error handlers will still catch errors
    setupErrorHandlersImmediately(
      this._logService,
      InstanaLoggingBootstrapper.NAME
    );

    try {
      // Then load Instana script asynchronously (non-blocking)
      await this.initializeInstana(config);

      this._earlyInitialized = true;
      this.logInformation(
        'Instana early initialization completed successfully.'
      );
    } catch (error: unknown) {
      this.logError('Instana early initialization has failed', error);
      // CRITICAL FAIL-SAFE: Always resolve to avoid blocking the main app
      // Error handlers are already set up, so errors will still be caught
    }
  }

  /**
   * Late initialization phase: Sets up user and route tracking.
   * This phase requires dependencies and should run after they are ready.
   */
  public async initializeLate(
    config?: InstanaLoggingBootstrapperConfig
  ): Promise<void> {
    if (!this._earlyInitialized) {
      this.logWarning(
        'Early initialization must complete before late initialization.'
      );
      return;
    }

    // Use stored config if not provided
    const activeConfig = config || this._config;
    if (!activeConfig) {
      this.logWarning('Config is not available for late initialization.');
      return;
    }

    try {
      // Set up dependency-based tracking
      await this._trackingService.setupUserTracking((error) => {
        this.logError('Failed to setup user tracking', error);
      });
      this._trackingService.setupRouteTracking((error) => {
        this.logError('Failed to setup route tracking', error);
      });

      this.logInformation(
        'Instana late initialization completed successfully.'
      );
    } catch (error: unknown) {
      this.logError('Instana late initialization has failed', error);
    }
  }

  /**
   * Legacy method for backward compatibility - calls both phases sequentially.
   * @deprecated Use initializeEarly() and initializeLate() separately instead.
   */
  public async initialize(
    config?: InstanaLoggingBootstrapperConfig
  ): Promise<void> {
    await this.initializeEarly(config);
    await this.initializeLate(config);
  }

  /**
   * Orchestrates Instana EUM initialization: checks if script already exists,
   * declares the queue, loads the script, and applies configuration.
   * Uses a Promise to signal completion to APP_INITIALIZER.
   */
  private async initializeInstana(
    config: InstanaLoggingBootstrapperConfig
  ): Promise<void> {
    try {
      // Apply config and return if Instana script is already loaded
      if (
        this._document?.getElementById(InstanaLoggingBootstrapper.SCRIPT_ID)
      ) {
        this.logWarning(
          'Instana EUM agent + script already present. Applying config.'
        );
        this._configService.applyConfig(config, (error) => {
          this.logError('Failed to apply Instana EUM configuration.', error);
        });
        return;
      }

      // Declare the Instana EUM queue before loading script
      const w = window as any;
      InstanaLoggingBootstrapper.declareInstanaEumQueue(w);
      this.logInformation('Instana EUM queue declared successfully.');

      // Queue the configuration BEFORE the script loads.
      // The official Instana snippet queues key and reportingUrl first, and the
      // agent reads them from the queue at boot. When the configuration arrives
      // only after the load event, the agent boots without a key and the first
      // beacon fails with HTTP 400.
      this._configService.applyConfig(config, (error) => {
        this.logError('Failed to apply Instana EUM configuration.', error);
      });

      // Load the script using the dedicated service
      // Promise resolves on success, rejects on failure
      await this._scriptLoader.loadScript(
        config,
        InstanaLoggingBootstrapper.SCRIPT_ID,
        () => {
          // Optional success callback
          this.logInformation('Instana EUM script loaded successfully.');
        },
        () => {
          // Optional failure callback (for logging before rejection)
          const timeoutMs =
            config.scriptLoadTimeout ?? DEFAULT_SCRIPT_LOAD_TIMEOUT_MS;
          this.logWarning(
            `Instana EUM script failed to load or timed out (${timeoutMs}ms) from ${config.scriptUrl}. Using console.log fallback.`
          );
        }
      );

      // The configuration is already queued; the agent consumed it at boot.
    } catch (error) {
      this.logError('Error during Instana EUM initialization.', error);
      // CRITICAL FAIL-SAFE: Always set up fallback to avoid breaking the app
      this._configService.setupConsoleFallback();
      this.logInformation(
        'Console.log fallback for Instana EUM API (ineum) has been set up.'
      );
    }
  }

  /**
   * Static method to set up global error handlers immediately, before Angular initialization.
   * This ensures error handlers are active before any other code runs.
   *
   * CRITICAL: This should be called at module load time, not during Angular initialization.
   *
   * @param logService Optional LogService for logging (uses console if not provided)
   * @deprecated Use setupErrorHandlersImmediately from instana-error-handlers module instead
   */
  public static setupErrorHandlersImmediately(logService?: LogService): void {
    setupErrorHandlersImmediately(logService, InstanaLoggingBootstrapper.NAME);
  }

  // Helper methods for logging (with fallback to console)
  private logInformation(message: string): void {
    if (this._logService) {
      this._logService.information(InstanaLoggingBootstrapper.NAME, message);
    } else {
      console.log(`[${InstanaLoggingBootstrapper.NAME}] ${message}`);
    }
  }

  private logWarning(message: string): void {
    if (this._logService) {
      this._logService.warning(InstanaLoggingBootstrapper.NAME, message);
    } else {
      console.warn(`[${InstanaLoggingBootstrapper.NAME}] ${message}`);
    }
  }

  private logError(message: string, error?: unknown): void {
    if (this._logService) {
      const errorObj =
        error instanceof Error ? error : new Error(String(error || message));
      this._logService.error(InstanaLoggingBootstrapper.NAME, errorObj);
    } else {
      console.error(`[${InstanaLoggingBootstrapper.NAME}] ${message}`, error);
    }
  }
}
